import { packNetAxios } from "@/lib/api-client";
import { type Batch, type PageOfBatches, pageOfBatchesSchema } from "@/schemas/routing/batch";
import {
	type SelectionPipelineConfiguration,
	selectionPipelineSchema,
	type SelectionStep,
} from "@/schemas/routing/selection-pipeline";
import type { SelectionAnalyticsData } from "@/schemas/audit/selection-analytics";
import type { ProductionGroupData } from "@/schemas/selection-pipeline-data";

import { baseRoutes } from "./base-routes";

export const removePackagingSolutions = async (packagingSolutionIds: string[]) => {
	if (!packagingSolutionIds.length) {
		return;
	}

	return packNetAxios.delete(`${baseRoutes.selection}/v1/selection/packagingSolutions`, {
		headers: { "Content-Type": "application/json" },
		data: packagingSolutionIds,
	});
};

export const triggerJobByBatchId = async (machineGroupId: string, batchId: string) => {
	return packNetAxios.put(
		`${baseRoutes.selection}/v1/triggerProduction/packagingSolutions/forMachineGroup/${machineGroupId}/forBatch/${batchId}/single`,
		null,
	);
};

export type TriggerByLpnRequest = {
	machineGroupId: string;
	licensePlateNumber: string;
};

export const triggerJobByLpnWithBody = async (machineGroupId: string, lpn: string) => {
	const request: TriggerByLpnRequest = { machineGroupId: machineGroupId, licensePlateNumber: lpn };
	return packNetAxios.put(
		`${baseRoutes.selection}/v1/triggerProduction/packagingSolutions/byLicensePlateNumber`,
		request,
	);
};

export const deleteBatchById = async (batchId: string) => {
	return packNetAxios.delete(`${baseRoutes.selection}/v1/selection/packagingSolutions/byBatchId/${batchId}`);
};

export const produceNext = async (machineGroupId: string, batchId: string) => {
	return packNetAxios.put(
		`${baseRoutes.selection}/v1/batch/machineGroupId/${machineGroupId}/batchId/${batchId}/BatchProduceNext`,
	);
};

export const produceNow = async (machineGroupId: string, batchId: string) => {
	return packNetAxios.post(
		`${baseRoutes.selection}/v1/batch/machineGroupId/${machineGroupId}/batchId/${batchId}/ProduceNow`,
	);
};

const selectionPipelineApi = `${baseRoutes.selection}/v1/SelectionPipelineConfigurations`;

export const getSelectionPipelines = async (): Promise<SelectionPipelineConfiguration[]> => {
	const response = await packNetAxios.get<SelectionPipelineConfiguration[]>(selectionPipelineApi);
	return response.data;
};

export const getSelectionPipeline = async (pipelineId: string): Promise<SelectionPipelineConfiguration> => {
	const response = await packNetAxios.get(`${selectionPipelineApi}/${pipelineId}`);

	if (response.status !== 200) {
		throw new Error(`Failed to fetch pipeline: ${response.status}`);
	}

	return selectionPipelineSchema.enrich({ callSite: "getSelectionPipeline" }).parseWithTelemetry(response.data);
};

export type CreateSelectionPipelineRequest = {
	tenantId?: string;
	dataPipeline: SelectionPipelineConfiguration["dataPipeline"];
};

export type UpdateSelectionPipelineRequest = {
	id: string;
	tenantId: string;
	dataPipeline: SelectionPipelineConfiguration["dataPipeline"];
};

export const createSelectionPipeline = async (
	data: CreateSelectionPipelineRequest,
): Promise<SelectionPipelineConfiguration> => {
	const response = await packNetAxios.post<SelectionPipelineConfiguration>(selectionPipelineApi, data);
	return response.data;
};

export const updateSelectionPipeline = async (
	data: UpdateSelectionPipelineRequest,
): Promise<SelectionPipelineConfiguration[]> => {
	const response = await packNetAxios.put<SelectionPipelineConfiguration[]>(`${selectionPipelineApi}/${data.id}`, data);
	return response.data;
};

export const deleteSelectionPipeline = async (pipelineId: string): Promise<void> => {
	await packNetAxios.delete(`${selectionPipelineApi}/${pipelineId}`);
};

interface GetAvailableBatchesParams {
	productionGroupId: string;
	machineGroupId: string | null;
	filter: string | null;
	pageNumber: number;
	pageSize: number;
	sortSteps: SelectionStep[];
	cartonPropertyGroups: string[] | null;
}

export const getAvailableBatches = async ({
	productionGroupId,
	machineGroupId,
	filter,
	pageNumber,
	pageSize,
	sortSteps,
	cartonPropertyGroups,
}: GetAvailableBatchesParams): Promise<PageOfBatches> => {
	const response = await packNetAxios.post(`${baseRoutes.selection}/v1/batch/availableBatches`, {
		productionGroupId,
		machineGroupId,
		filter,
		pageNumber,
		pageSize,
		sortSteps,
		cartonPropertyGroups,
	});

	if (response.status !== 200) {
		throw new Error(`Failed to fetch batches: ${response.status}`);
	}

	pageOfBatchesSchema.enrich({ callSite: "getAvailableBatches" }).safeParseWithTelemetry(response.data);

	return response.data;
};

export type ActiveBatchStatus = "Producing" | "Pausing" | "Paused" | "Completed" | "Waiting to trigger";

export interface ActiveBatch extends Omit<Batch, "status"> {
	status: ActiveBatchStatus;
}

function convertToActiveBatch(newBatchResponse: Batch, scanToTriggerSingleConfigured: boolean): ActiveBatch {
	let computedStatus: ActiveBatchStatus;
	switch (newBatchResponse.status) {
		case "ProducingNow":
		case "Selected":
			computedStatus =
				scanToTriggerSingleConfigured && newBatchResponse.selectedCount === 0 ? "Waiting to trigger" : "Producing";
			break;
		case "Completed":
			computedStatus = "Completed";
			break;
		case "Paused":
			computedStatus = newBatchResponse.selectedCount > 0 ? "Pausing" : "Paused";
			break;
		default:
			computedStatus = "Producing";
			break;
	}

	const activeBatch: ActiveBatch = {
		...newBatchResponse,
		status: computedStatus,
	};
	return activeBatch;
}

interface GetActiveBatchesParams {
	machineGroupId: string;
	scanToTriggerSingleConfigured: boolean;
}

export const getActiveBatches = async ({
	machineGroupId,
	scanToTriggerSingleConfigured,
}: GetActiveBatchesParams): Promise<ActiveBatch[]> => {
	const response = await packNetAxios.get<PageOfBatches>(
		`${baseRoutes.selection}/v1/batch/activeBatches/${machineGroupId}`,
	);
	return response.data.results.map((r) => convertToActiveBatch(r, scanToTriggerSingleConfigured));
};

interface GetProductionGroupAnalyticDetailsPage {
	productionGroupId: string;
	pageNumber: number;
	pageSize: number;
}

export interface SelectionAnalyticsDetailsJob {
	InternalUniqueId: string;
	Alias: string;
	LicensePlateNumber: string;
	Cartons: unknown[];
	ReleasedForProductionDate: string;
	ImportDate: string;
	CreateByDate: string;
}

export interface SelectionAnalyticsDetailsPage {
	productionGroup?: {
		alias?: string;
	};
	totalResults: number;
	jobs: SelectionAnalyticsDetailsJob[];
}

export const getProductionGroupAnalyticDetailsPage = async ({
	productionGroupId,
	pageNumber,
	pageSize,
}: GetProductionGroupAnalyticDetailsPage): Promise<SelectionAnalyticsDetailsPage> => {
	const response = await packNetAxios.get<SelectionAnalyticsDetailsPage>(
		`${baseRoutes.selection}/v1/selection/analyticsDetails/${productionGroupId}/page/${pageNumber}/pageSize/${pageSize}`,
	);

	return response.data;
};

export async function getSelectionAnalytics(): Promise<SelectionAnalyticsData[]> {
	const response = await packNetAxios.get<SelectionAnalyticsData[]>(`${baseRoutes.selection}/v1/selection/analytics`);
	return response.data;
}

export async function getProductionGroupData(): Promise<ProductionGroupData[]> {
	const response = await packNetAxios.get<ProductionGroupData[]>(
		`${baseRoutes.selection}/v1/SelectionPipelineConfigurations/ProductionGroupData`,
	);
	return response.data;
}

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.selection}/v1/Selection/version`);
	return response.data;
}
